import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Input,
    InputGroup,
    InputGroupText,
    Row,
} from "reactstrap";
import ItemsTable from "../../items/components/ItemsTable";
import ModulesTableQuote from "../../modules/components/ModulesTableQuote";
import { useTheme } from "../../../hooks/useTheme";
import { formatCurrency } from "../../../utils/numberUtils";
import QuoteCartTable from "./quoteCartTable";
import LegacyQuotesTable from './legacyQuotesTable';
import QuoteImporter from "./quoteImporter";
import { fetchModuleData } from "../../modules/data/moduleService";
import PolicyCheck from '../../../components/policies/PolicyCheck';
import ModuleInputsModal from "../../modules/components/ModuleInputsModal";
import BasketItemModal from "../../items/components/BasketItemModal";
import { fetchItemLookup } from "../../items/data/itemLookupService";
import { useQuery } from "@tanstack/react-query";
import { fetchTags } from "../../tags/data/tagService";
import { getAllItemLookupTypes } from "../../items/data/itemLookupTypeService";
import { getBlankQuoteItem } from "../functions/quoteFunctions";
import ModuleInputControl from "../../modules/components/ModuleInputControl";
import { QuoteDataContext } from "../contexts/quoteDataContext";
import { toast } from "react-toastify";

const QuoteBuilder = ({ quoteItems, quoteRevisionId, activeBasketProcessingModules, basketProcessingInputValues, addQuoteItems, addQuoteItem, deleteQuoteItem, deleteQuoteItems, updateQuoteItem, quoteModules, addQuoteModule, deleteQuoteModule, updateQuoteModule, decoupleQuoteModule, handleBasketProcessingInputValueChanged, updateQuoteItems, updateQuoteItemTypeAttribute }) => {
    const theme = useTheme();

    const [isModuleInputModalOpen, setIsModuleInputModalOpen] = useState(false);
    const [isBasketItemModalOpen, setIsBasketItemModalOpen] = useState(false)
    const [currentActiveMainTab, setCurrentActiveMainTab] = useState("Add Items");

    const LAYOUT_MODE_KEY = "quoteBuilder.layoutMode";
    const [layoutMode, setLayoutMode] = useState(() => {
        try {
            const saved = typeof window !== "undefined" && localStorage.getItem(LAYOUT_MODE_KEY);
            return saved === "Vertical" || saved === "Horizontal" ? saved : "Horizontal";
        } catch {
            return "Horizontal";
        }
    });

    // Non-module basket item that is being edited
    const [selectedQuoteItem, setSelectedQuoteItem] = useState();

    // Module that's on the quote, so this is where we store/fetch the inputs used on the modal.
    // Note that this is a byReference copy of one of the entries in the quoteModules state array
    // So we can update properties on either one.
    const [selectedQuoteModule, setSelectedQuoteModule] = useState();

    // Module that's currently being added/edited.  This is where the module logic can be found.
    // This object contains all of the module revisions, so we can check if the quote is using the latest revision, and also we can pick any one
    // to run the input modal / processing logic against (In code at least - we won't let the user choose)
    const [selectedProductModule, setSelectedProductModule] = useState();

    // This is used to select the module revision that we are going to use in the module inputs Modal
    const [selectedProductModuleRevisionId, setSelectedProductModuleRevisionId] = useState({});

    // Calculated Property.  Passed to the Module Inputs modal, so it knows what inputs to show, and what logic to run
    const selectedProductModuleRevision = useMemo(
        () => selectedProductModule?.moduleRevisions.find(x => x.id === selectedProductModuleRevisionId),
        [selectedProductModule, selectedProductModuleRevisionId]
    );

    const quoteDataContext = React.useContext(QuoteDataContext);

    const [rightWidth, setRightWidth] = useState(() => {
        const saved = localStorage.getItem("quoteBuilder.rightWidth");
        return saved ? parseFloat(saved) : 40; // fallback to 40% if nothing saved
    });

    useEffect(() => {
        try {
            localStorage.setItem(LAYOUT_MODE_KEY, layoutMode);
        } catch (e) { }
    }, [layoutMode]);

    /*
        DB Queries
    */

    // Fetch Tags using useQuery
    const {
        data: tagsData,
        isLoading: isLoadingTags,
        error: tagsError,
    } = useQuery({
        queryKey: ["tagsFilter"],
        queryFn: fetchTags,
        staleTime: 60000
    });

    // Fetch Item Lookup Types using useQuery
    const {
        data: itemLookupTypesData,
        isLoading: isLoadingItemLookupTypes,
        error: ItemLookupTypesError,
    } = useQuery({
        queryKey: ["itemLookupTypesFilter"],
        queryFn: getAllItemLookupTypes,
        staleTime: 60000
    });

    /*
        EVENT HANDLERS
    */

    const handleAddItemToBasketClicked = async (itemLookupId) => {
        const item = await fetchItemLookup(itemLookupId)

        const activeVersion = item.itemLookupVersions
            .filter(r => new Date(r.activeStartDate) < new Date())
            .sort((a, b) => new Date(b.activeStartDate) - new Date(a.activeStartDate))[0];

        var newBasketItem = {
            ...item,
            guid: crypto.randomUUID(),
            description: "",
            source: "Catalogue",
            internalName: item.name,
            originalNameOnQuote: "", // No longer used, but need to supply a value, even though we don't use it, or API will reject it
            nameOnQuote: "", // No longer used
            tags: item.itemLookupTags.map(t => t.tagId),
            quantity: 1,
            autotaskCode: item.autotaskCode ?? "", /* Some items in the DB have NULL in this field, which we will replace by empty string */
            itemLookupId: itemLookupId,
            supplierId: item.supplierId,
            itemLookupVersionId: activeVersion.id,
            customAttributes: item.customAttributes ? JSON.parse(item.customAttributes) : [],
            typeCustomAttributes: item.typeCustomAttributes ? JSON.parse(item.typeCustomAttributes) : [],
            quoteModuleGuid: null,
            quoteGroupDescription: itemLookupTypesData?.find(x => x.id === item.itemLookupTypeId)?.itemLookupTypeName ?? "Not Found",
            ...activeVersion,
            originalUpfrontCost: activeVersion.upfrontCost,
            originalUpfrontPrice: activeVersion.upfrontPrice,
            originalRecurringCost: activeVersion.recurringCost,
            originalRecurringPrice: activeVersion.recurringPrice,
            upfrontNameOnQuote: activeVersion.quoteUpfrontDescription ?? "",
            recurringNameOnQuote: activeVersion.quoteRecurringDescription ?? ""
        }

        // Remove unwanted properties from the resulting object that were copied from the activeVersion
        delete newBasketItem.upfrontCostDefaultDiscount
        delete newBasketItem.upfrontCostDiscountThreshold
        delete newBasketItem.upfrontPriceDefaultDiscount
        delete newBasketItem.upfrontPriceDiscountThreshold
        delete newBasketItem.recurringCostDefaultDiscount
        delete newBasketItem.recurringCostDiscountThreshold
        delete newBasketItem.recurringPriceDefaultDiscount
        delete newBasketItem.recurringPriceDiscountThreshold
        delete newBasketItem.activeStartDate
        delete newBasketItem.createdAt
        delete newBasketItem.updatedAt
        delete newBasketItem.deleteDate
        delete newBasketItem.itemLookup
        delete newBasketItem.name // This is renamed to internalName above
        delete newBasketItem.displayName // This is renamed to nameOnQuote above
        delete newBasketItem.id // Database will generate this

        // Make a copy of the starting costs/prices, so we can see if the user changed it, and to measure the discount level
        newBasketItem.originalUpfrontCost = newBasketItem.upfrontCost
        newBasketItem.originalUpfrontPrice = newBasketItem.upfrontPrice
        newBasketItem.originalRecurringost = newBasketItem.recurringCost
        newBasketItem.originalRecurringPrice = newBasketItem.recurringPrice

        addQuoteItem(newBasketItem)

        //        alert(`clicked: \n\n${item.id} - ${item.name}\n\n ${JSON.stringify(item.typeCustomAttributes, null, 2)}`)
    }

    const handleAddModuleToBasketClicked = async (moduleId) => {
        const moduleData = await fetchModuleData(moduleId)

        if (!moduleData) {
            toast.error("Failed to load module id " + moduleId)
        } else {
            setSelectedProductModule(moduleData)

            var activeModuleRevision = moduleData.moduleRevisions.find(x => x.disabled === false)

            if (!activeModuleRevision) {
                setSelectedProductModuleRevisionId(0)
                toast.error("No active module revision found")
            } else {
                setSelectedProductModuleRevisionId(activeModuleRevision.id)
                setIsModuleInputModalOpen(true)
            }
        }
    }

    const handleAddCustomItemClick = () => {
        let newQuoteItem = getBlankQuoteItem()
        newQuoteItem.source = "Custom"

        setSelectedQuoteItem(newQuoteItem)
        setIsBasketItemModalOpen(true)
    }

    // Keep a ref to the latest quoteItems and quoteModules so that callbacks
    // with stable references (e.g. those captured inside quoteBasket's columns 
    // useMemo with [] deps) can always read the current array without causing
    // the memo to recompute.
    const quoteItemsRef = useRef(quoteItems);
    const quoteModulesRef = useRef(quoteModules);
    quoteItemsRef.current = quoteItems;
    quoteModulesRef.current = quoteModules;

    const handleEditItemInBasketClick = useCallback(async (basketItemGuid) => {
        const item = quoteItemsRef.current?.find(x => x.guid === basketItemGuid)

        if (!item) {
            toast.error("Couldn't find quote item with guid " + basketItemGuid)
            return
        }

        setSelectedQuoteItem(item)
        console.log('handleEditItemInBasketClick', basketItemGuid)
        setIsBasketItemModalOpen(true)
    }, []) // stable reference — reads latest quoteItems via quoteItemsRef

    const handleEditModuleInBasketClick = useCallback(async (quoteModuleGuid) => {
        const quoteModule = quoteModulesRef.current?.find(x => x.guid === quoteModuleGuid)

        if (quoteModule) {
            const moduleData = await fetchModuleData(quoteModule.moduleId)

            if (!moduleData) {
                alert("Failed to load module id " + quoteModule.moduleId)
            } else {
                setSelectedProductModule(moduleData)

                var activeModuleRevision = moduleData.moduleRevisions.find(x => x.disabled === false)

                if (!activeModuleRevision) {
                    alert("Warning: You are modifying a module for which there is currently no active revision.  Please check this is ok to sell")

                    setSelectedProductModuleRevisionId(quoteModule.moduleRevisionId)
                } else {
                    if (activeModuleRevision.id !== quoteModule.moduleRevisionId) {
                        alert("Warning: This basket item was added with a different revision of this module.  It will be upgraded to the latest revision")
                    }

                    setSelectedProductModuleRevisionId(activeModuleRevision.id)
                    setIsModuleInputModalOpen(true)
                }
            }

            console.log('quoteModule', quoteModule)

            setSelectedQuoteModule(quoteModule)
            setIsModuleInputModalOpen(true)
        } else {
            alert("Couldn't find quote module " + quoteModuleGuid)
        }
    }, []) // stable reference — reads latest quoteModules via quoteModulesRef

    const handleDeleteItemFromBasketClick = useCallback(async (itemGuid) => {
        const basketItemToRemove = quoteItemsRef.current?.find(x => x.guid === itemGuid)

        if (basketItemToRemove) {
            await deleteQuoteItem(basketItemToRemove)
        } else {
            toast.error("Couldn't find quote item to remove")
        }
    }, []) // stable reference — reads latest quoteItems via quoteItemsRef

    const handleDeleteQuoteItemsFromBasket = useCallback(async (items) => {
        const basketItemsToRemove = quoteItemsRef.current?.filter(x => items.some(i => i.guid === x.guid))

        if (!basketItemsToRemove || basketItemsToRemove.length === 0) {
            toast.error("Couldn't find quote items to remove")
            return
        }

        await deleteQuoteItems(basketItemsToRemove)
    }, []) // stable reference — reads latest quoteItems via quoteItemsRef

    const handleDeleteModuleFromBasketClick = useCallback(async (quoteModuleGuid) => {
        const basketItemsFromThisModule = quoteItemsRef.current?.filter(x => x.quoteModuleGuid === quoteModuleGuid)

        if (!basketItemsFromThisModule || basketItemsFromThisModule.length === 0) {
            toast.error("Couldn't find quote items to remove")
            return
        }

        // Remove all basket items that were generated by the module
        for (const quoteItem of basketItemsFromThisModule) {
            await deleteQuoteItem(quoteItem)
        }

        const quoteModule = quoteModulesRef.current?.find(x => x.guid === quoteModuleGuid)

        if (quoteModule) {
            // Remove module from list of modules in the basket
            await deleteQuoteModule(quoteModule)
        } else {
            alert("Couldn't find quote module " + quoteModuleGuid)
        }
    }, []) // stable reference — reads latest quoteItems and quoteModules via refs

    const handleDecoupleModuleClick = (quoteModuleGuid, groupName) => {
        decoupleQuoteModule(quoteModuleGuid, groupName);
    }

    const handleBasketItemModalSubmit = (updatedItem, isNewItem) => {
        if (isNewItem) {
            addQuoteItem({ ...updatedItem, source: "Custom" })
        } else {
            updateQuoteItem(updatedItem)
        }
    }

    const handleModuleInputModalSubmit = async ({ inputValues, basketItems, acknowledgedWarnings }) => {
        if (!selectedProductModule || !selectedProductModuleRevision) {
            console.error("Missing selected product module or revision");
            return;
        }

        const warnings = acknowledgedWarnings.map(w => ({ ...w, acknowledgedBy: quoteDataContext.currentUser.userGuid }))

        console.log("Module Input Modal Submit:", { inputValues, basketItems, acknowledgedWarnings });

        let quoteModuleGuid = selectedQuoteModule?.guid
        const foundModule = quoteModules.find(x => x.guid === quoteModuleGuid);
        const updatedQuoteModule = foundModule ? structuredClone(foundModule) : null;

        if (updatedQuoteModule) {
            // Editing an existing quote module, so update input values

            updatedQuoteModule.inputValues = inputValues
            updatedQuoteModule.warnings = warnings

            // add the form inputs parameter to enable guid lookups in auditing
            let moduleRevisionFormInputs = {}
            if (selectedProductModuleRevision.moduleLogicJson) {
                const moduleRevisionModuleLogicJson = JSON.parse(selectedProductModuleRevision.moduleLogicJson)
                moduleRevisionFormInputs = moduleRevisionModuleLogicJson.form.inputs
            }

            updateQuoteModule(updatedQuoteModule, moduleRevisionFormInputs)
        } else {
            // New module for this quote, so add to the list, along with the inputs
            quoteModuleGuid = crypto.randomUUID()

            addQuoteModule({
                guid: quoteModuleGuid,
                moduleId: selectedProductModule.id,
                moduleRevisionId: selectedProductModuleRevision.id,
                inputValues,
                warnings: warnings
            })
        }

        var moduleGeneratedItems = basketItems.map(x => ({
            ...x,
            quoteModuleGuid: quoteModuleGuid,
            moduleId: selectedProductModule.id,
            moduleRevisionId: selectedProductModuleRevision.id,
            quoteRevisionId: quoteRevisionId
        }))

        const quoteItemsAlreadyAddedByThisModule = quoteItems.filter(x => x.quoteModuleGuid === quoteModuleGuid)

        // Remove items that were previously generated by the module that are not there any more after the new inputs
        const itemsToRemove = quoteItemsAlreadyAddedByThisModule.filter(existingItem => !moduleGeneratedItems.some(itemToAdd => itemToAdd.guid === existingItem.guid) && existingItem.quoteModuleGuid === quoteModuleGuid)

        const quoteItemsByGuid = new Map(quoteItemsAlreadyAddedByThisModule.map(item => [item.guid, item]));

        // Add/Update basket items generated by module
        for (const quoteItem of moduleGeneratedItems) {
            const existing = quoteItemsByGuid.get(quoteItem.guid);

            if (existing) {
                // We want to keep any existing cost/price overrides that the user made, so copy those values across
                await updateQuoteItem({
                    ...quoteItem,
                    id: existing.id,

                    // If cost/price hasn't been overrideen then take the new value from the module, otherwise leave what the user changed
                    // We are still changing original costs/price fields (below), so we can warn user if any new pricing, combined with their overrides
                    // has resulted in discounts out of range, or poor profitability.
                    upfrontCost: existing.upfrontCost === existing.originalUpfrontCost ? quoteItem.upfrontCost : existing.upfrontCost,
                    recurringCost: existing.recurringCost === existing.originalRecurringCost ? quoteItem.recurringCost : existing.recurringCost,
                    upfrontPrice: existing.upfrontPrice === existing.originalUpfrontPrice ? quoteItem.upfrontPrice : existing.upfrontPrice,
                    recurringPrice: existing.recurringPrice === existing.originalRecurringPrice ? quoteItem.recurringPrice : existing.recurringPrice,

                    // If the user hasn't changed the name on quote from the original value, then update it with the new value from the module, otherwise leave what they changed
                    upfrontNameOnQuote: existing.upfrontNameOnQuote === existing.originalUpfrontNameOnQuote || existing.originalUpfrontNameOnQuote === null || existing.originalUpfrontNameOnQuote === "" ? quoteItem.upfrontNameOnQuote : existing.upfrontNameOnQuote,
                    recurringNameOnQuote: existing.recurringNameOnQuote === existing.originalRecurringNameOnQuote || existing.originalRecurringNameOnQuote === null || existing.originalRecurringNameOnQuote === "" ? quoteItem.recurringNameOnQuote : existing.recurringNameOnQuote,

                    // Update latest originals name on quote fields from the module, so we can compare against these
                    originalUpfrontNameOnQuote: quoteItem.upfrontNameOnQuote,
                    originalRecurringNameOnQuote: quoteItem.recurringNameOnQuote,

                    quoteRevisionId: existing.quoteRevisionId,
                    originalUpfrontCost: quoteItem.upfrontCost,
                    originalRecurringCost: quoteItem.recurringCost,
                    originalUpfrontPrice: quoteItem.upfrontPrice,
                    originalRecurringPrice: quoteItem.recurringPrice
                });
            } else {
                await addQuoteItem({ ...quoteItem });
            }
        };

        // delete basket items that are no longer relevant
        for (const quoteItem of itemsToRemove) {
            await deleteQuoteItem(quoteItem)
        };

        setSelectedQuoteModule(undefined)
        setSelectedProductModuleRevisionId(0);
    }

    const handleModuleInputModalCancelled = () => {
        setSelectedQuoteModule()
        setSelectedProductModule();
        setSelectedProductModuleRevisionId(0);
    }

    const quoteColumns = [
        {
            key: "description",
            label: "Description",
        },
        {
            key: "upfrontCost",
            label: "Init Cost",
            render: (value) => (

                <InputGroup >
                    <InputGroupText style={{ fontSize: "0.8rem" }}>£</InputGroupText>
                    <Input style={{ fontSize: "0.8rem" }} disabled className="px-3 text-end" type="text"
                        value={formatCurrency(value)} />
                </InputGroup>

            ),
        },
        {
            key: "recurringCost",
            label: "Recurring Cost",
            render: (value) => (
                <InputGroup>
                    <InputGroupText style={{ fontSize: "0.8rem" }}>£</InputGroupText>
                    <Input style={{ fontSize: "0.8rem" }} disabled className="px-3 text-end" type="text"
                        value={formatCurrency(value)} />
                </InputGroup>
            ),
        },
        {
            key: "quantity",
            label: "Quantity",
            render: (value) => (
                <InputGroup style={{ width: "9rem" }}>
                    <Button color={theme === "dark-mode" ? "dark" : "light"} outline>
                        <i className="bi bi-dash"></i>
                    </Button>
                    <Input value={value} />
                    <Button color={theme === "dark-mode" ? "dark" : "light"} outline>
                        <i className="bi bi-plus"></i>
                    </Button>
                </InputGroup>
            ),
        },
        {
            key: "id",
            label: "",
            className: "text-end",
            render: () => (
                <div className="d-flex align-items-center gap-2">
                    <Button color="danger" outline>
                        <i className="bi bi-trash"></i>
                    </Button>
                </div>
            ),
        },
    ];

    const displayTab = (tabName) => {
        return (
            <h6
                key={tabName}
                className={
                    currentActiveMainTab === tabName
                        ? "custom-nav-selected custom-nav m-3"
                        : "custom-nav m-3"
                }
                style={{ cursor: "pointer" }}
                onClick={() => setCurrentActiveMainTab(tabName)}
            >
                {tabName}
            </h6>
        )        
    }

    return (
        <>
            {
                layoutMode === "Horizontal" ? (
                    <>
                        {/* Horizontal Mode */}
                        <Row>
                            <Card className="h-100">
                                <QuoteCartTable
                                    quoteItems={quoteItems}
                                    updateQuoteItems={updateQuoteItems}
                                    updateQuoteItemTypeAttribute={updateQuoteItemTypeAttribute}
                                    onClickEditModule={handleEditModuleInBasketClick}
                                    onClickDeleteModule={handleDeleteModuleFromBasketClick}
                                    onClickDecoupleModule={handleDecoupleModuleClick}
                                    onClickEditItem={handleEditItemInBasketClick}
                                    onClickDeleteItem={handleDeleteItemFromBasketClick}
                                    onClickDeleteItems={handleDeleteQuoteItemsFromBasket}
                                    onClickAddCustomItem={handleAddCustomItemClick}
                                    layoutMode={layoutMode}
                                    setLayoutMode={setLayoutMode}
                                    updateQuoteItem={updateQuoteItem}
                                    quoteModules={quoteModules}
                                />
                            </Card>
                        </Row>
                        <Row>
                            <Card className="h-100">
                                <CardHeader>
                                    <div style={{ display: "flex" }}>
                                        {["Add Items", "Add Modules", "Import Items", "Legacy Quotes", "Settings"].map((tab) => (
                                            <React.Fragment key={tab}>
                                                {displayTab(tab)}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </CardHeader>
                                <CardBody className="mt-0 pt-0">
                                    {currentActiveMainTab === "Add Items" &&
                                        <ItemsTable
                                            mode="User"
                                            onAddItemToBasketClicked={handleAddItemToBasketClicked}
                                        />
                                    }
                                    {currentActiveMainTab === "Add Modules" &&
                                        <ModulesTableQuote
                                            onAddModuleToBasketClicked={handleAddModuleToBasketClicked}
                                        />
                                    }
                                    {currentActiveMainTab === "Import Items" && (
                                        <QuoteImporter addQuoteItems={addQuoteItems} />
                                    )}
                                    {currentActiveMainTab === "Legacy Quotes" && (                                        
                                        <LegacyQuotesTable quoteRevisionId={quoteRevisionId} addQuoteItems={addQuoteItems}/>
                                    )}
                                    {
                                        currentActiveMainTab === "Settings" && activeBasketProcessingModules.length > 0 && (
                                            activeBasketProcessingModules.map(module => (
                                                module.inputs.length > 0 &&
                                                <fieldset key={module.guid} className="mb-3">
                                                    <legend className="fs-6" style={{ padding: '0 10px' }}>{module.name}</legend>
                                                    <hr></hr>
                                                    {module.inputs.map(input => (
                                                        <ModuleInputControl
                                                            key={input.guid}
                                                            inputGuid={input.guid}
                                                            inputs={module.inputs}
                                                            values={basketProcessingInputValues?.filter(x => x.moduleId === module.id) ?? []}
                                                            isOnVisiblePage={true}
                                                            handleInputValueChanged={(inputGuid, newValue) => { handleBasketProcessingInputValueChanged(module.id, inputGuid, newValue) }}
                                                            handleInputValidityChanged={() => { }}
                                                            handleInputWarningsChanged={() => { }}
                                                            processedRuleResultsMap={[]}
                                                        />
                                                    ))
                                                    }
                                                </fieldset>
                                            ))
                                        )
                                    }
                                </CardBody>
                            </Card>
                        </Row>
                    </>
                ) : (
                    <>
                        {/* Vertical Mode */}
                        <div style={{ display: "flex", height: "100%", minHeight: "500px" }}>
                            {/* Left panel */}
                            <div style={{ width: `${100 - rightWidth}%`, paddingRight: "0.5rem" }}>
                                <Card className="h-100">
                                    <CardHeader>
                                    <div style={{ display: "flex" }}>
                                        {["Add Items", "Add Modules", "Import Items", "Legacy Quotes", "Settings"].map((tab) => (                                           
                                            <React.Fragment key={tab}>
                                                {displayTab(tab)}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                    </CardHeader>
                                    <CardBody className="mt-0 pt-0">
                                        {currentActiveMainTab === "Add Items" &&
                                            <ItemsTable
                                                mode="User"
                                                onAddItemToBasketClicked={handleAddItemToBasketClicked}
                                            />
                                        }
                                        {currentActiveMainTab === "Add Modules" &&
                                            <ModulesTableQuote
                                                onAddModuleToBasketClicked={handleAddModuleToBasketClicked}
                                            />
                                        }
                                        {currentActiveMainTab === "Import Items" && (
                                            <QuoteImporter addQuoteItems={addQuoteItems} />
                                        )}
                                        {currentActiveMainTab === "Legacy Quotes" && (
                                            <LegacyQuotesTable quoteRevisionId={quoteRevisionId} addQuoteItems={addQuoteItems} />
                                        )}
                                        {
                                            currentActiveMainTab === "Settings" && activeBasketProcessingModules.length > 0 && (
                                                activeBasketProcessingModules.map(module => (
                                                    module.inputs.length > 0 &&
                                                    <fieldset key={module.guid} className="mb-3">
                                                        <legend className="fs-6" style={{ padding: '0 10px' }}>{module.name}</legend>
                                                        <hr></hr>
                                                        {module.inputs.map(input => (
                                                            <ModuleInputControl
                                                                key={input.guid}
                                                                inputGuid={input.guid}
                                                                inputs={module.inputs}
                                                                values={basketProcessingInputValues?.filter(x => x.moduleId === module.id) ?? []}
                                                                isOnVisiblePage={true}
                                                                handleInputValueChanged={(inputGuid, newValue) => { handleBasketProcessingInputValueChanged(module.id, inputGuid, newValue) }}
                                                                handleInputValidityChanged={() => { }}
                                                                handleInputWarningsChanged={() => { }}
                                                                processedRuleResultsMap={[]}
                                                            />
                                                        ))
                                                        }
                                                    </fieldset>
                                                ))
                                            )
                                        }
                                    </CardBody>
                                </Card>
                            </div>

                            {/* Resizer */}
                            <div
                                className="resizer"
                                style={{
                                    width: "6px",
                                    cursor: "col-resize",
                                    zIndex: 10,
                                }}
                                onMouseDown={(e) => {
                                    const startX = e.clientX;
                                    const startWidth = rightWidth;

                                    // Disable text selection
                                    document.body.style.userSelect = "none";

                                    const onMouseMove = (moveEvent) => {
                                        const dx = moveEvent.clientX - startX;
                                        const newWidth = startWidth - (dx / window.innerWidth) * 100;

                                        if (newWidth >= 5 && newWidth <= 80) { // allow right panel to grow to 95%
                                            setRightWidth(newWidth);
                                            localStorage.setItem("quoteBuilder.rightWidth", newWidth.toFixed(2));
                                        }
                                    };

                                    const onMouseUp = () => {
                                        document.removeEventListener("mousemove", onMouseMove);
                                        document.removeEventListener("mouseup", onMouseUp);
                                        document.body.style.userSelect = "auto"; // Re-enable selection
                                    };

                                    document.addEventListener("mousemove", onMouseMove);
                                    document.addEventListener("mouseup", onMouseUp);
                                }}
                            />

                            {/* Right panel */}
                            <div style={{ width: `${rightWidth}%`, paddingLeft: "0.5rem" }}>
                                <Card className="h-100">
                                    <QuoteCartTable
                                        quoteItems={quoteItems}
                                        updateQuoteItems={updateQuoteItems}
                                        updateQuoteItemTypeAttribute={updateQuoteItemTypeAttribute}
                                        onClickEditModule={handleEditModuleInBasketClick}
                                        onClickDeleteModule={handleDeleteModuleFromBasketClick}
                                        onClickDecoupleModule={handleDecoupleModuleClick}
                                        onClickEditItem={handleEditItemInBasketClick}
                                        onClickDeleteItem={handleDeleteItemFromBasketClick}
                                        onClickDeleteItems={handleDeleteQuoteItemsFromBasket}
                                        onClickAddCustomItem={handleAddCustomItemClick}
                                        layoutMode={layoutMode}
                                        setLayoutMode={setLayoutMode}
                                        updateQuoteItem={updateQuoteItem}
                                        quoteModules={quoteModules}
                                    />
                                </Card>
                            </div>
                        </div>
                    </>
                )
            }
            {isModuleInputModalOpen && selectedProductModule && selectedProductModuleRevision &&
                <ModuleInputsModal
                    selectedModule={selectedProductModule}
                    moduleRevisionId={selectedProductModuleRevision.id}
                    selectedModuleRevisionLogic={JSON.parse(
                        selectedProductModuleRevision.moduleLogicJson
                    )}
                    initialInputValues={selectedQuoteModule?.inputValues}
                    isModuleInputModalOpen={isModuleInputModalOpen} setIsModuleInputModalOpen={setIsModuleInputModalOpen}
                    handleFormSubmit={handleModuleInputModalSubmit}
                    handleFormCancelled={handleModuleInputModalCancelled}
                />
            }

            {/* Modal for displaying selected item details */}
            {selectedQuoteItem && isBasketItemModalOpen && <BasketItemModal
                basketItemModalIsOpen={isBasketItemModalOpen} setBasketItemModalIsOpen={(isOpen) => {
                    setSelectedQuoteItem()
                    setIsBasketItemModalOpen(isOpen)
                }}
                item={selectedQuoteItem}
                onItemChange={(updatedItem, isNewItem) => handleBasketItemModalSubmit(updatedItem, isNewItem)}
            />}
        </>
    );
};

export default QuoteBuilder;
